import { useState } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { CreateAccountPayload } from "@/store/api";
import type { Role } from "@/types";

interface CreateAccountModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: CreateAccountPayload) => Promise<void>;
}

interface FormState {
  role: Role;
  names: string;
  surnames: string;
  email: string;
  password: string;
  cedula: string;
  phone: string;
  city: string;
}

const initial: FormState = {
  role: "admin",
  names: "",
  surnames: "",
  email: "",
  password: "",
  cedula: "",
  phone: "",
  city: "",
};

// Formulario para que un administrador cree cuentas nuevas (admin o asesor).
const CreateAccountModal = ({ open, onClose, onCreate }: CreateAccountModalProps) => {
  const [form, setForm] = useState<FormState>(initial);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const close = () => {
    setForm(initial);
    setError("");
    onClose();
  };

  const submit = async () => {
    setError("");
    if (!form.names.trim() || !form.surnames.trim()) {
      setError("Escribe los nombres y apellidos de la cuenta.");
      return;
    }
    if (!form.email.trim() || !form.email.includes("@")) {
      setError("Ingresa un correo electrónico válido.");
      return;
    }
    if (form.password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres.");
      return;
    }

    setBusy(true);
    try {
      await onCreate({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        names: form.names.trim(),
        surnames: form.surnames.trim(),
        role: form.role,
        cedula: form.cedula.trim(),
        phone: form.phone.trim(),
        city: form.city.trim(),
      });
      setForm(initial);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo crear la cuenta.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Crear cuenta"
      icon="ri-user-add-line"
      footer={
        <>
          <button onClick={close} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} disabled={busy} className={primaryButtonClass}>
            {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-user-add-line" />}
            {busy ? "Creando..." : "Crear cuenta"}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div>
          <label className={labelClass}>Tipo de cuenta</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(
              [
                { key: "admin" as const, title: "Administrador", desc: "Acceso total a la plataforma", icon: "ri-vip-crown-2-line" },
                { key: "asesor" as const, title: "Asesor", desc: "Cuenta aprobada lista para su portal", icon: "ri-user-star-line" },
              ]
            ).map((opt) => {
              const active = form.role === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => set({ role: opt.key })}
                  className={`text-left rounded-md border px-3 py-3 cursor-pointer transition-colors ${
                    active
                      ? "border-accent-500/60 bg-accent-500/10"
                      : "border-background-300/50 hover:bg-background-200/40"
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                    <i className={`${opt.icon} text-accent-300`} />
                    {opt.title}
                  </span>
                  <span className="block text-[11px] text-foreground-500 mt-1">{opt.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-accent-300 mb-2">
            Datos de la cuenta
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Nombres</label>
              <input value={form.names} onChange={(e) => set({ names: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Apellidos</label>
              <input value={form.surnames} onChange={(e) => set({ surnames: e.target.value })} className={inputClass} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Correo electrónico</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                placeholder="cuenta@correo.com"
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Contraseña inicial (mín. 6)</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => set({ password: e.target.value })}
                placeholder="••••••••"
                className={inputClass}
              />
              <p className="text-[10px] text-foreground-500 mt-1">
                Comparte esta contraseña con la persona. Podrá cambiarla luego desde su cuenta.
              </p>
            </div>
            <div>
              <label className={labelClass}>Cédula (opcional)</label>
              <input value={form.cedula} onChange={(e) => set({ cedula: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Celular (opcional)</label>
              <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className={inputClass} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Ciudad (opcional)</label>
              <input value={form.city} onChange={(e) => set({ city: e.target.value })} className={inputClass} />
            </div>
          </div>
        </div>

        {error && (
          <p className="text-xs text-primary-200 bg-primary-500/15 border border-primary-400/30 rounded-md px-3 py-2 flex items-center gap-2 animate-fade-up">
            <i className="ri-error-warning-line" /> {error}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default CreateAccountModal;